/**
 * M5 — Mapa GPS de OTs del día
 * ═══════════════════════════════════════════════════════════════════
 * Vista consolidada tipo dispatcher:
 *   • Mapa Leaflet centrado en Ecuador (Quito por defecto)
 *   • Marcadores color-coded por estado de la OT
 *   • Filtros: fecha, técnico, zona, estado
 *   • Popup con datos clave + botones "Abrir OT" / "Waze" / "Google Maps"
 *   • Panel lateral con listado sincronizado (click centra el mapa)
 *   • Contadores por estado en la parte superior
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import api from "../lib/api";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Calendar as CalIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Filter,
  MapPin,
  Navigation as NavIcon,
  Route,
  RefreshCcw,
  ExternalLink,
  Users,
  X,
} from "lucide-react";

const STATUS_COLOR = {
  pendiente_agendamiento: "#94A3B8",
  asignada: "#2563EB",
  en_ruta: "#0891B2",
  en_ejecucion: "#0EA5E9",
  pendiente_fiscalizacion: "#D97706",
  validada: "#16A34A",
  con_observaciones: "#DC2626",
  rechazada: "#B91C1C",
  prefacturada: "#059669",
  cerrada: "#0A3747",
  cancelada: "#6B7280",
};
const STATUS_LABEL = {
  pendiente_agendamiento: "Pte. agendam.",
  asignada: "Asignada",
  en_ruta: "En ruta",
  en_ejecucion: "Ejecutando",
  pendiente_fiscalizacion: "Pte. fiscaliz.",
  validada: "Validada",
  con_observaciones: "Con obs.",
  rechazada: "Rechazada",
  prefacturada: "Prefacturada",
  cerrada: "Cerrada",
  cancelada: "Cancelada",
};

const buildIcon = (color, priority) =>
  L.divIcon({
    className: "",
    html: `
      <div style="position:relative">
        <div style="
          background:${color};
          color:white;
          width:32px;height:32px;
          border-radius:50% 50% 50% 0;
          transform:rotate(-45deg);
          display:flex;align-items:center;justify-content:center;
          border:2px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,.35);
        ">
          <div style="transform:rotate(45deg);font-size:10px;font-weight:bold">${priority || "•"}</div>
        </div>
      </div>`,
    iconSize: [32, 42],
    iconAnchor: [16, 32],
    popupAnchor: [0, -30],
  });

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/* Centra el mapa cuando se hace click en un item del sidebar */
function FocusHandler({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus && focus.lat != null && focus.lon != null) {
      map.flyTo([focus.lat, focus.lon], 15, { duration: 0.6 });
    }
  }, [focus, map]);
  return null;
}

/* Auto-fit bounds cuando cambian las OT */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

export default function MapaOTsDia() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [techs, setTechs] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTech, setFilterTech] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [focus, setFocus] = useState(null);
  const [route, setRoute] = useState(null); // v3.2.0 · ruta del técnico seleccionado
  const [routeLoading, setRouteLoading] = useState(false);
  const iso = toISODate(anchor);

  // v3.2.0 · Al cambiar técnico/día, buscar su ruta (polyline + mini-calendar)
  useEffect(() => {
    if (!filterTech) { setRoute(null); return; }
    const tech = techs.find((t) => String(t.id) === String(filterTech));
    if (!tech?.email) { setRoute(null); return; }
    setRouteLoading(true);
    api.get(`/v1/technician/${encodeURIComponent(tech.email)}/route-day`, { params: { date: iso } })
      .then((r) => setRoute({ ...r.data, tech_name: tech.name, tech_email: tech.email }))
      .catch(() => setRoute(null))
      .finally(() => setRouteLoading(false));
  }, [filterTech, iso, techs]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordRes, techRes, zoneRes] = await Promise.all([
        api.get("/v1/work-orders", {
          params: { from_date: iso, to_date: iso, page: 1, page_size: 500, include_unscheduled: true },
        }),
        api.get("/v1/users/technicians").catch(() => ({ data: { items: [] } })),
        api.get("/v1/config/zones").catch(() => ({ data: { items: [] } })),
      ]);
      setOrders(ordRes.data.items || []);
      setTechs(techRes.data.items || techRes.data || []);
      const zList = zoneRes.data?.items || zoneRes.data || [];
      setZones(Array.isArray(zList) ? zList : []);
    } catch (e) {
      toast.error("Error al cargar OTs");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [iso]);

  // Sólo OTs con coordenadas para mostrar en mapa
  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (o.lat == null || o.lon == null) return false;
      if (filterTech) {
        const tid = o.technician_id || o.assigned_user_id;
        if (String(tid) !== filterTech) return false;
      }
      if (filterZone && o.zone_id !== filterZone) return false;
      if (filterStatus && o.status !== filterStatus) return false;
      return true;
    });
  }, [orders, filterTech, filterZone, filterStatus]);

  const noCoords = orders.length - filteredOrders.length -
    orders.filter((o) => {
      // Contar solo los filtrados que ya tenían coords pero no pasaron filtro
      if (o.lat == null || o.lon == null) return false;
      if (filterTech && String(o.technician_id || o.assigned_user_id) !== filterTech) return true;
      if (filterZone && o.zone_id !== filterZone) return true;
      if (filterStatus && o.status !== filterStatus) return true;
      return false;
    }).length;

  // Contadores por estado (sobre OTs filtradas)
  const statusCounts = useMemo(() => {
    const c = {};
    for (const o of filteredOrders) {
      c[o.status] = (c[o.status] || 0) + 1;
    }
    return c;
  }, [filteredOrders]);

  const points = filteredOrders.map((o) => ({
    id: o.id,
    lat: parseFloat(o.lat),
    lon: parseFloat(o.lon),
    order: o,
  }));

  // Centro default: Quito
  const defaultCenter = [-0.1807, -78.4678];

  return (
    <Layout
      title="Mapa GPS de OTs"
      subtitle="Vista día · marcadores color-coded por estado"
      wide
      actions={
        <button
          onClick={fetchData}
          className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
          title="Refrescar"
          data-testid="map-refresh"
        >
          <RefreshCcw size={14} />
        </button>
      }
    >
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnchor(addDays(anchor, -1))}
            className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
            data-testid="map-prev-day"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setAnchor(new Date())}
            className="px-3 py-1.5 text-xs font-bold rounded bg-[#0A3747] text-[#E0E621]"
            data-testid="map-today"
          >
            HOY
          </button>
          <button
            onClick={() => setAnchor(addDays(anchor, 1))}
            className="p-2 border border-gray-300 bg-white rounded hover:bg-gray-50"
            data-testid="map-next-day"
          >
            <ChevronRight size={14} />
          </button>
          <div className="ml-3 text-sm font-bold text-[#0A3747] uppercase tracking-wider">
            <CalIcon size={14} className="inline mr-2" />
            {anchor.toLocaleDateString("es-EC", {
              timeZone: "America/Guayaquil", weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filterTech}
            onChange={(e) => setFilterTech(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
            data-testid="map-tech-filter"
          >
            <option value="">Todos los técnicos</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            value={filterZone}
            onChange={(e) => setFilterZone(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
            data-testid="map-zone-filter"
          >
            <option value="">Todas las zonas</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-2 py-1.5 border border-gray-300 rounded text-xs bg-white"
            data-testid="map-status-filter"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Contadores por estado */}
      <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 mb-3 flex flex-wrap items-center gap-3">
        <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">
          Resumen
        </div>
        <div
          className="flex items-center gap-1.5 text-xs"
          data-testid="map-count-total"
        >
          <MapPin size={12} className="text-gray-500" />
          <span className="font-bold text-[#0A3747]">
            {filteredOrders.length}
          </span>
          <span className="text-gray-500">visibles</span>
        </div>
        {Object.entries(statusCounts).map(([k, v]) => (
          <div
            key={k}
            className="flex items-center gap-1.5 text-[11px]"
            data-testid={`map-count-${k}`}
          >
            <span
              className="w-3 h-3 rounded-full border border-white shadow"
              style={{ backgroundColor: STATUS_COLOR[k] || "#64748B" }}
            />
            <span className="text-gray-700">{STATUS_LABEL[k] || k}</span>
            <span className="font-bold text-[#0A3747]">{v}</span>
          </div>
        ))}
        {orders.filter((o) => o.lat == null || o.lon == null).length > 0 && (
          <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 ml-auto">
            {orders.filter((o) => o.lat == null || o.lon == null).length} OT sin coordenadas (no visibles en mapa)
          </div>
        )}
      </div>

      {/* Grid: Mapa + Sidebar */}
      <div className="flex gap-3" style={{ height: "calc(100vh - 300px)" }}>
        {/* Mapa */}
        <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden relative" data-testid="map-container">
          {loading && (
            <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70">
              <Loader2 className="animate-spin text-gray-500" size={32} />
            </div>
          )}
          <MapContainer
            center={defaultCenter}
            zoom={12}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap"
            />
            {points.length > 0 && <FitBounds points={points} />}
            {focus && <FocusHandler focus={focus} />}
            {route?.route?.length > 1 && (
              <Polyline
                positions={route.route.filter(s => s.lat != null && s.lon != null).map((s) => [s.lat, s.lon])}
                pathOptions={{ color: "#DC2626", weight: 4, opacity: 0.8, dashArray: "6 4" }}
                data-testid="route-polyline"
              />
            )}
            {points.map((p) => {
              const o = p.order;
              const color = STATUS_COLOR[o.status] || "#64748B";
              // v3.2.0 · Si hay ruta activa, badge con el seq (número de parada)
              const seqBadge = route?.route?.find((s) => String(s.odoo_task_id) === String(o.odoo_task_id))?.seq;
              return (
                <Marker
                  key={p.id}
                  position={[p.lat, p.lon]}
                  icon={buildIcon(color, seqBadge || o.priority)}
                >
                  <Popup>
                    <OrderPopup order={o} onOpen={() => navigate(`/ordenes/${o.id}`)} />
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Sidebar con lista */}
        <div className="w-80 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
          {/* v3.2.0 · Panel de ruta si hay técnico seleccionado */}
          {filterTech && (
            <div className="border-b border-gray-200 bg-gradient-to-br from-red-50 to-white" data-testid="tech-route-panel">
              <div className="px-3 py-2 bg-[#DC2626] text-white flex items-center gap-2">
                <Route size={14} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold uppercase tracking-wider truncate">
                    Ruta · {route?.tech_name || "Cargando…"}
                  </div>
                  <div className="text-[9px] font-mono opacity-90">
                    {routeLoading ? "Consultando…" : route ? `${route.stops_count} paradas · ${route.total_distance_km} km` : "Sin datos"}
                  </div>
                </div>
                <button
                  onClick={() => setFilterTech("")}
                  className="text-white/80 hover:text-white"
                  title="Limpiar técnico"
                  data-testid="tech-route-close"
                >
                  <X size={14} />
                </button>
              </div>
              {/* Mini-calendar / timeline vertical */}
              <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                {routeLoading ? (
                  <div className="text-center p-3"><Loader2 size={14} className="animate-spin inline text-gray-400"/></div>
                ) : !route || route.stops_count === 0 ? (
                  <div className="text-[11px] text-gray-500 italic text-center py-3">
                    Sin OTs agendadas para este técnico hoy
                  </div>
                ) : (
                  route.route.map((stop) => {
                    const t = stop.scheduled_at ? new Date(stop.scheduled_at) : null;
                    const hh = t ? t.toLocaleTimeString("es-EC", { timeZone: "America/Guayaquil", hour: "2-digit", minute: "2-digit" }) : "—";
                    return (
                      <div
                        key={`route-${stop.odoo_task_id}`}
                        className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-red-50 cursor-pointer border-l-2 border-red-400"
                        onClick={() => setFocus({ lat: stop.lat, lon: stop.lon, id: stop.odoo_task_id })}
                        data-testid={`route-stop-${stop.seq}`}
                      >
                        <div className="w-6 h-6 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {stop.seq}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 text-[10px] font-mono">
                            <Clock size={9} className="text-gray-400" />
                            <span className="font-bold text-[#0A3747]">{hh}</span>
                            <span className="text-gray-500 truncate">· {stop.task_number || stop.odoo_task_id}</span>
                          </div>
                          <div className="text-[11px] font-medium text-gray-800 truncate">{stop.client_name || "—"}</div>
                          <div className="text-[9px] text-gray-500 truncate">{stop.activity_type || ""} · {stop.internal_status}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
          <div className="px-3 py-2 bg-[#0A3747] text-white flex items-center gap-2">
            <Users size={14} />
            <div className="flex-1">
              <div className="text-[11px] font-bold uppercase tracking-wider">
                OTs del día
              </div>
              <div className="text-[9px] font-mono opacity-80">
                {filteredOrders.length} visible · click para centrar
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {filteredOrders.length === 0 ? (
              <div className="text-[11px] text-gray-400 italic text-center py-8">
                Sin OTs para los filtros actuales
              </div>
            ) : (
              filteredOrders.map((o) => (
                <OrderListItem
                  key={o.id}
                  order={o}
                  onClick={() =>
                    setFocus({ lat: parseFloat(o.lat), lon: parseFloat(o.lon), id: o.id })
                  }
                  onOpen={() => navigate(`/ordenes/${o.id}`)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function OrderListItem({ order, onClick, onOpen }) {
  const color = STATUS_COLOR[order.status] || "#64748B";
  const t = order.scheduled_at ? new Date(order.scheduled_at) : null;
  const time = t
    ? `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}`
    : "—";

  return (
    <div
      className="border border-gray-200 rounded p-2 hover:shadow cursor-pointer"
      onClick={onClick}
      data-testid={`list-order-${order.code || order.id.slice(0, 8)}`}
    >
      <div className="flex items-center justify-between gap-1 text-[10px] font-mono mb-0.5">
        <span className="flex items-center gap-1">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="text-gray-700">{STATUS_LABEL[order.status] || order.status}</span>
        </span>
        <span className="font-bold text-[#0A3747]">{time}</span>
        {order.priority && (
          <span className="text-red-700 font-bold">{order.priority}</span>
        )}
      </div>
      <div className="text-[11px] font-bold text-gray-900 truncate">
        {order.code || order.id.slice(0, 8)}
      </div>
      <div className="text-[10px] text-gray-600 truncate">
        {order.client_name || "—"}
      </div>
      {order.technician_name && (
        <div className="text-[9px] text-gray-500 truncate">
          👤 {order.technician_name}
        </div>
      )}
      <div className="flex items-center justify-between mt-1 gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="text-[9px] font-bold text-[#0A3747] hover:underline flex items-center gap-1"
          data-testid={`list-open-${order.code || order.id.slice(0, 8)}`}
        >
          <ExternalLink size={9} /> Abrir OT
        </button>
        {order.lat != null && order.lon != null && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${order.lat},${order.lon}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-[9px] font-bold text-blue-700 hover:underline flex items-center gap-1"
          >
            <NavIcon size={9} /> Maps
          </a>
        )}
      </div>
    </div>
  );
}

function OrderPopup({ order, onOpen }) {
  const color = STATUS_COLOR[order.status] || "#64748B";
  const t = order.scheduled_at ? new Date(order.scheduled_at) : null;
  return (
    <div className="text-xs min-w-[200px]" data-testid={`popup-${order.code}`}>
      <div className="flex items-center gap-2 mb-1">
        <span
          className="inline-block w-3 h-3 rounded-full border border-white shadow"
          style={{ backgroundColor: color }}
        />
        <span className="font-mono font-bold text-[#0A3747]">
          {order.code || order.id.slice(0, 8)}
        </span>
        {order.priority && (
          <span className="text-[9px] font-bold text-red-700 border border-red-200 rounded px-1">
            {order.priority}
          </span>
        )}
      </div>
      <div className="font-semibold text-gray-900">{order.client_name || "Cliente"}</div>
      {order.address && (
        <div className="text-[10px] text-gray-600 mt-0.5">{order.address}</div>
      )}
      <div className="text-[10px] text-gray-600 mt-1">
        <div>Estado: <b>{STATUS_LABEL[order.status] || order.status}</b></div>
        {t && (
          <div>
            Fecha:{" "}
            <b>
              {t.toLocaleString("es-EC", {
                timeZone: "America/Guayaquil", dateStyle: "short",
                timeStyle: "short",
              })}
            </b>
          </div>
        )}
        {order.technician_name && (
          <div>
            Técnico: <b>{order.technician_name}</b>
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <button
          onClick={onOpen}
          className="text-[10px] px-2 py-1 rounded bg-[#0A3747] text-white font-bold flex items-center gap-1"
        >
          <ExternalLink size={10} /> Abrir OT
        </button>
        {order.lat != null && order.lon != null && (
          <>
            <a
              href={`https://waze.com/ul?ll=${order.lat},${order.lon}&navigate=yes`}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] px-2 py-1 rounded bg-cyan-600 text-white font-bold flex items-center gap-1"
            >
              <NavIcon size={10} /> Waze
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${order.lat},${order.lon}`}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] px-2 py-1 rounded bg-blue-600 text-white font-bold flex items-center gap-1"
            >
              <NavIcon size={10} /> Maps
            </a>
          </>
        )}
      </div>
    </div>
  );
}
